import { useState, useEffect, useCallback, useRef } from 'react';
import { Property, PropertySearchParams } from '@/types/property';
import { getProperties, getFeaturedProperties, getProperty, getSimilarProperties } from '@/lib/api';
import { useTenant } from './useTenant';

export function useProperties(params?: Partial<PropertySearchParams>) {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isLoading = useRef(false);
  const { config } = useTenant();

  // Serialize params to avoid unnecessary re-fetches
  const paramsKey = JSON.stringify(params);
  const companyId = config?.id;

  const fetchProperties = useCallback(async () => {
    if (isLoading.current) return;

    // Wait for tenant config to load
    if (!companyId) {
      setLoading(false);
      return;
    }

    try {
      isLoading.current = true;
      setLoading(true);
      setError(null);

      const response = await getProperties(params, companyId);
      if (response.success) {
        setProperties(response.data);
      } else {
        setError('Error al cargar propiedades');
      }
    } catch (err) {
      setError('Error al cargar propiedades');
    } finally {
      setLoading(false);
      isLoading.current = false;
    }
  }, [paramsKey, companyId]); // Add companyId as dependency

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  return { properties, loading, error, refetch: fetchProperties };
}

export function useFeaturedProperties() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isLoading = useRef(false);
  const isInitialized = useRef(false);
  const { config } = useTenant();
  const companyId = config?.id;

  const fetchFeatured = useCallback(async () => {
    if (isLoading.current) return;

    // Wait for tenant config to load
    if (!companyId) {
      setLoading(false);
      return;
    }

    try {
      isLoading.current = true;
      setLoading(true);
      setError(null);

      const response = await getFeaturedProperties(companyId);
      if (response.success) {
        setProperties(response.data);
      } else {
        setError('Error al cargar propiedades destacadas');
      }
    } catch (err) {
      setError('Error al cargar propiedades destacadas');
    } finally {
      setLoading(false);
      isLoading.current = false;
      isInitialized.current = true;
    }
  }, [companyId]); // Add companyId as dependency

  useEffect(() => {
    if (!isInitialized.current && companyId) {
      fetchFeatured();
    }
  }, [fetchFeatured, companyId]); // Add companyId and fetchFeatured as dependencies

  return { properties, loading, error, refetch: fetchFeatured };
}

export function useProperty(id: string) {
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isLoading = useRef(false);

  const fetchProperty = useCallback(async (propertyId: string) => {
    if (isLoading.current) return;

    try {
      isLoading.current = true;
      setLoading(true);
      setError(null);

      const response = await getProperty(propertyId);
      if (response.success) {
        setProperty(response.data);
      } else {
        setError(response.message || 'Propiedad no encontrada');
      }
    } catch (err) {
      setError('Error al cargar la propiedad');
    } finally{
      setLoading(false);
      isLoading.current = false;
    }
  }, []); // No dependencies - stable function

  useEffect(() => {
    if (id) {
      fetchProperty(id);
    }
  }, [id, fetchProperty]);

  return { property, loading, error, refetch: () => fetchProperty(id) };
}

export function useSimilarProperties(propertyId: string, limit = 3) {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isLoading = useRef(false);
  const { config } = useTenant();
  const companyId = config?.id;

  const fetchSimilar = useCallback(async (id: string, limitCount: number) => {
    if (isLoading.current) return;

    // Wait for tenant config to load
    if (!companyId) {
      setLoading(false);
      return;
    }

    try {
      isLoading.current = true;
      setLoading(true);
      setError(null);

      const response = await getSimilarProperties(id, limitCount, companyId);
      if (response.success) {
        setProperties(response.data);
      } else {
        setError('Error al cargar propiedades similares');
      }
    } catch (err) {
      setError('Error al cargar propiedades similares');
    } finally {
      setLoading(false);
      isLoading.current = false;
    }
  }, [companyId]); // Add companyId as dependency

  useEffect(() => {
    if (propertyId && companyId) {
      fetchSimilar(propertyId, limit);
    }
  }, [propertyId, limit, fetchSimilar, companyId]); // Add companyId as dependency

  return { properties, loading, error, refetch: () => fetchSimilar(propertyId, limit) };
}